import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { usePublicProgram } from '../hooks/usePrograms';
import { useDances } from '../hooks/useDances';

export function PublicProgramPage() {
  const { publicId } = useParams<{ publicId: string }>();
  const { data: program, isLoading, isError } = usePublicProgram(publicId);
  const { data: allDances } = useDances('ALL');
  const [printView, setPrintView] = useState(false);

  if (isLoading) return <div style={{ maxWidth: '800px', margin: '0 auto', padding: '2rem', textAlign: 'center' }}>Loading program...</div>;

  if (isError || !program) {
    return (
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '2rem', textAlign: 'center' }}>
        <p>This program is not available or has been made private.</p>
        <Link to="/" className="btn btn-secondary">Go Home</Link>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', padding: 'var(--spacing-xl)' }}>
      <div className="card" style={{ backgroundColor: 'white', padding: 'var(--spacing-xl)' }}>
        <h1 style={{ margin: '0 0 var(--spacing-sm)' }}>{program.title}</h1>
        <p style={{ color: '#666', margin: '0 0 var(--spacing-lg)', fontSize: '0.9rem' }}>
          by {program.username} · {program.dances.length} dance{program.dances.length !== 1 ? 's' : ''}
        </p>

        {program.description && (
          <p style={{ marginBottom: 'var(--spacing-xl)', color: '#555' }}>{program.description}</p>
        )}

        <div style={{ display: 'flex', gap: 'var(--spacing-md)', marginBottom: 'var(--spacing-lg)' }} className="no-print">
          <button
            onClick={() => setPrintView(!printView)}
            className="btn btn-secondary"
          >
            {printView ? 'Compact View' : 'Print View'}
          </button>
          <button onClick={() => window.print()} className="btn btn-primary">
            Print
          </button>
        </div>

        {program.dances.length === 0 ? (
          <p style={{ color: '#666', fontStyle: 'italic' }}>No dances in this program.</p>
        ) : (
          <div style={{ display: 'grid', gap: '0.75rem' }}>
            {program.dances.map((slot, index) => {
              const dance = allDances?.find(d => d.danceId === slot.danceId);
              return (
                <div key={slot.danceId} style={{
                  padding: 'var(--spacing-md)',
                  border: '1px solid #e0e0e0',
                  borderRadius: 'var(--radius-sm)',
                  backgroundColor: '#fafafa'
                }}>
                  <div style={{ display: 'flex', alignItems: 'start', gap: 'var(--spacing-md)' }}>
                    <span style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#999', minWidth: '2rem' }}>{index + 1}</span>
                    <div style={{ flex: 1 }}>
                      {dance ? (
                        <>
                          <Link to={`/dances/${dance.danceId}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                            <strong>{dance.title}</strong>
                          </Link>
                          {dance.choreographer && <span style={{ color: '#666', fontSize: '0.875rem' }}> by {dance.choreographer}</span>}
                          <div style={{ marginTop: '0.25rem' }}>
                            <span style={{ fontSize: '0.875rem', color: '#555' }}>{dance.formation}</span>
                          </div>
                          {printView && (
                            <div className="program-dance-notation" style={{ marginTop: 'var(--spacing-md)' }}>
                              <div className="dance-section dance-section-a1">
                                <div className="dance-section-header">A1</div>
                                <p style={{ margin: 0, fontSize: '0.95rem' }}>{dance.a1}</p>
                              </div>
                              <div className="dance-section dance-section-a2">
                                <div className="dance-section-header">A2</div>
                                <p style={{ margin: 0, fontSize: '0.95rem' }}>{dance.a2}</p>
                              </div>
                              <div className="dance-section dance-section-b1">
                                <div className="dance-section-header">B1</div>
                                <p style={{ margin: 0, fontSize: '0.95rem' }}>{dance.b1}</p>
                              </div>
                              <div className="dance-section dance-section-b2">
                                <div className="dance-section-header">B2</div>
                                <p style={{ margin: 0, fontSize: '0.95rem' }}>{dance.b2}</p>
                              </div>
                              {dance.callerNotes && (
                                <div style={{ marginTop: 'var(--spacing-sm)', fontSize: '0.875rem', color: '#856404', fontStyle: 'italic' }}>
                                  Caller notes: {dance.callerNotes}
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      ) : (
                        <strong style={{ color: '#999' }}>Dance unavailable</strong>
                      )}
                      {slot.callerNotes && (
                        <div style={{ marginTop: '0.25rem', fontSize: '0.875rem', color: '#856404', fontStyle: 'italic' }}>
                          {slot.callerNotes}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
